import { css, type Theme } from '@emotion/react';
import styled from '@emotion/styled';
import ArrowNext from '@engineering/icons/arrow-next';
import { Button, Ui } from '@silvertours/front-shared';

/**
 * These global styles are required by
 * [`react-day-picker`](https://react-day-picker.js.org/).
 * They are applied to the date picker on both search forms for
 * BM and CG, but also to the widgets under `/widget_search`.
 */
const GlobalStyles = ({
  theme,
  vertical,
  isSelectingRange = false,
}: {
  theme: Theme;
  vertical: boolean;
  isSelectingRange?: boolean;
}) => css`
  /* stylelint-disable selector-class-pattern */
  .rdp-root {
    position: relative;
    outline: 0;
    color: ${theme.colors.neutral.grey160};
    background-color: ${theme.colors.neutral.white100};
    font-family: ${theme.fontFamily.default};
    user-select: none;

    ${vertical &&
    css`
      padding-block: ${theme.spacing[40]};
      background:
        linear-gradient(
          ${theme.colors.neutral.white100} 30%,
          rgb(255 255 255 / 0%)
        ),
        linear-gradient(
            rgb(255 255 255 / 0%),
            ${theme.colors.neutral.white100} 70%
          )
          0 100%,
        radial-gradient(farthest-side at 50% 0, rgb(0 0 0 / 20%), transparent),
        radial-gradient(
            farthest-side at 50% 100%,
            rgb(0 0 0 / 20%),
            transparent
          )
          0 100%,
        linear-gradient(${theme.colors.neutral.white100});
      background-attachment: local, local, scroll, scroll, scroll;
      background-repeat: no-repeat;
      background-size:
        100% 40px,
        100% 40px,
        100% 14px,
        100% 14px,
        auto;
      overflow-y: scroll;
    `}
  }

  .rdp-nav {
    position: absolute;
    width: 100%;
  }

  .rdp-button_previous,
  .rdp-button_next {
    position: absolute;
    top: 0.625rem;
    display: ${vertical ? 'none' : ''};
    width: 1.5rem;
    height: 1.5rem;
    margin: 0;
    padding: 0;
    border: 0;
    outline: 0;
    font: inherit;
    background-color: transparent;
    -webkit-tap-highlight-color: transparent;
    box-sizing: border-box;
    appearance: none;
    cursor: pointer;

    &[aria-disabled='true'] {
      opacity: 0.4;
      cursor: not-allowed;
    }

    &:focus-visible {
      border-radius: 100%;
      outline: 0.125rem dashed ${theme.colors.brand.orange100};
      outline-offset: 0.25rem;
    }
  }

  .rdp-button_previous {
    left: 0.75rem;
  }

  .rdp-button_next {
    right: 0.75rem;
  }

  .rdp-months {
    position: relative;
    display: grid;
    grid-template-columns: ${vertical
      ? 'repeat(auto-fit, minmax(296px, 1fr))'
      : 'repeat(2, 1fr)'};
    gap: 1.25rem;
    margin: ${vertical ? 'auto' : '0.5rem 1.5rem'};
    padding-inline: ${vertical ? '1rem' : ''};

    #widget-search & {
      grid-template-columns: 1fr;
      padding-inline: 0.5rem;
    }
  }

  .rdp-month_caption {
    margin: 0.625rem 0.5rem;
    font-family: ${theme.fontFamily.alternate};
    font-weight: bold;
    font-size: ${theme.fontSize.sm.size};
    line-height: 1.5;
    text-align: center;
    text-transform: capitalize;
  }

  .rdp-month_grid {
    border-spacing: 0;
    border-collapse: collapse;
    width: 100%;
  }

  .rdp-weekday {
    display: table-cell;
    padding: calc(0.5rem + 0.3438rem) 0 0.3438rem;
    color: ${theme.colors.neutral.grey120};
    font-weight: normal;
    font-size: 0.875rem;
    line-height: 1.5;
    text-align: center;
    text-transform: uppercase;
  }

  .rdp-day_button {
    position: relative;
    display: block;
    margin: 0;
    padding: 0;
    border: 0;
    outline: 0;
    width: 100%;
    height: 2rem;
    font: inherit;
    font-size: ${theme.fontSize.sm.size};
    text-align: center;
    color: ${theme.colors.neutral.grey160};
    background: none;
    appearance: none;
    cursor: pointer;

    &:focus-visible::after {
      content: '';
      position: absolute;
      top: 0;
      left: 50%;
      border-radius: 100%;
      outline: 0.125rem dashed ${theme.colors.brand.orange100};
      outline-offset: 0.25rem;
      width: 2rem;
      height: 2rem;
      transform: translateX(-50%);
      pointer-events: none;
    }
  }

  .rdp-cell {
    padding: 0.5rem 0 0;
  }

  .rdp-day {
    padding: 0.5rem 0 0;

    &.rdp-hidden .rdp-day_button {
      position: absolute;
      width: 1px;
      height: 1px;
      margin: -1px;
      padding: 0;
      border: 0;
      overflow: hidden;
      clip-path: rect(0 0 0 0);
    }

    &.rdp-outside .rdp-day_button {
      color: ${theme.colors.brand.green20};
      cursor: default;
    }

    &.rdp-disabled .rdp-day_button {
      color: ${theme.colors.neutral.grey120};
      cursor: default;
    }

    &.rdp-day_sunday .rdp-day_button {
      color: ${theme.colors.brand.green100};
      font-weight: 600;
    }

    &.rdp-today:not(.rdp-selected) .rdp-day_button {
      margin: 0 auto;
      padding-top: 0.125rem;
      border-bottom: 0.125rem solid ${theme.colors.brand.green100};
      width: 2rem;
    }

    /* selected pick-up/drop-off dates */
    &.rdp-selected {
      &:not(.rdp-disabled, .rdp-outside) {
        &.rdp-range_start .rdp-day_button {
          position: relative;
          color: ${theme.colors.neutral.white100};
          background-image:
            radial-gradient(
              circle at center,
              ${theme.colors.brand.green100},
              ${theme.colors.brand.green100} 0.975rem,
              transparent 1rem
            ),
            linear-gradient(
              to right,
              transparent,
              transparent 49%,
              ${theme.colors.brand.green20} 50%
            );
        }

        &.rdp-range_end .rdp-day_button {
          position: relative;
          color: ${theme.colors.neutral.white100};
          background-image:
            radial-gradient(
              circle at center,
              ${theme.colors.brand.green100},
              ${theme.colors.brand.green100} 0.975rem,
              transparent 1rem
            ),
            linear-gradient(
              to left,
              transparent,
              transparent 49%,
              ${theme.colors.brand.green20} 50%
            );
        }

        &.rdp-range_start.rdp-range_end .rdp-day_button {
          position: relative;
          color: ${theme.colors.neutral.white100};
          background-image: radial-gradient(
            circle at center,
            ${theme.colors.brand.green120},
            ${theme.colors.brand.green120} 0.975rem,
            transparent 1rem
          );
        }
      }

      /* range between pick-up and drop-off */
      &:not(.rdp-range_start, .rdp-range_end, .rdp-outside) .rdp-day_button {
        color: ${theme.colors.neutral.grey160};
        background-color: ${theme.colors.brand.green20};
      }
    }

    ${!isSelectingRange &&
    css`
      &:not(.rdp-disabled, .rdp-outside, .rdp-range_start, .rdp-range_end):hover
        .rdp-day_button {
        &::after {
          content: '';
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          width: 2rem;
          height: 2rem;
          border-radius: 50%;
          border: 2px solid ${theme.colors.brand.green100};
          pointer-events: none;
          z-index: 1;
        }
      }

      /* Hide today border when ring shows to prevent jump */
      &.rdp-today:not(
          .rdp-disabled,
          .rdp-range_start,
          .rdp-range_end,
          .rdp-outside
        ):hover
        .rdp-day_button {
        border-bottom: 0.125rem solid transparent;
      }
    `}
  }

  @media screen and (min-width: 300px) {
    .rdp-day {
      min-width: 2.5rem;
    }
  }

  /* large widgets only */
  @media screen and ((width: 800px) or (width: 970px)) and (height: 250px) {
    .rdp-month {
      margin-top: 0.125rem;
    }

    .rdp-nav_button {
      top: -0.125rem;
    }

    .rdp-head_cell {
      padding-top: 4px;
      padding-bottom: 4px;
    }

    .rdp-cell {
      padding-top: 4px;
    }
  }

  /* stylelint-enable selector-class-pattern */
`;

const Wrapper = styled.dialog<{ hasHeader: boolean; hasFooter: boolean }>`
  position: static;
  display: ${({ hasHeader, hasFooter }) =>
    hasHeader || hasFooter ? 'grid' : 'flex'};
  /* stylelint-disable named-grid-areas-no-invalid */
  grid-template: ${({ hasHeader, hasFooter }) => {
    if (hasHeader && hasFooter) {
      return 'auto 1fr auto / 1fr';
    }
    if (hasHeader) {
      return 'auto 1fr / 1fr';
    }
    if (hasFooter) {
      return '1fr auto / 1fr';
    }
    return null;
  }};
  /* stylelint-enable named-grid-areas-no-invalid */
  flex-direction: column;
  padding: 0;
  border: 0;
  width: auto;
  height: auto;
  color: inherit;

  ${({ theme }) => theme.mq('sm')} {
    padding: 1.5rem 0;
  }
`;

const StyledHeader = styled.header`
  position: relative;
  display: grid;
  grid-template-columns: 1fr 1rem 1fr;
  place-items: center;
  padding: ${({ theme }) => theme.spacing[40]};
  border-bottom: 1px solid ${({ theme }) => theme.colors.neutral.grey80};
  color: ${({ theme }) => theme.colors.neutral.grey160};
  background-color: ${({ theme }) => theme.colors.neutral.white100};
  font-weight: 600;
  font-size: ${({ theme }) => theme.fontSize.sm.size};
  line-height: 1.5;

  #widget-search & {
    grid-template-columns: max-content 1rem max-content;
    justify-content: start;
    font-size: ${({ theme }) => theme.fontSize.xs.size};
  }
`;

const StyledHintText = styled.div`
  color: #222;
  text-align: center;
  font-size: ${({ theme }) => theme.fontSize.sm.size};
  line-height: ${({ theme }) => theme.fontSize.sm.lineHeight};
  background-color: ${({ theme }) => theme.colors.neutral.white100};
  padding: 0 0 ${({ theme }) => theme.spacing[30]};
  margin: 0 ${({ theme }) => theme.spacing[60]};
  border-bottom: 1px solid ${({ theme }) => theme.colors.neutral.grey100};
`;

const StyledSummaryText = styled.div`
  color: #222;
  text-align: center;
  font-size: ${({ theme }) => theme.fontSize.sm.size};
  line-height: ${({ theme }) => theme.fontSize.sm.lineHeight};
  background-color: ${({ theme }) => theme.colors.neutral.white100};
  padding: ${({ theme }) => theme.spacing[30]} 0 0;
  margin: 0 ${({ theme }) => theme.spacing[60]};
  border-top: 1px solid ${({ theme }) => theme.colors.neutral.grey100};
`;

const StyledFooter = styled.footer<{ vertical: boolean }>`
  padding: ${({ theme }) => theme.spacing[40]};
  border-top: 1px solid ${({ theme }) => theme.colors.neutral.grey80};
  background-color: ${({ theme }) => theme.colors.neutral.white100};

  #widget-search & {
    padding: ${({ theme }) => theme.spacing[20]};
  }
`;

const Arrow = styled(ArrowNext)`
  width: 1rem;
  height: 1rem;
  fill: currentcolor;
`;

const ConfirmationButton = styled(Button)`
  margin-inline: auto;
  max-width: 50%;

  #widget-search & {
    padding: 0.5rem 0.75rem;
    max-width: none;
  }
`;

const CloseButton = styled(Ui.IconButton)`
  position: absolute;
  top: 1rem;
  right: 1rem;
`;

export {
  GlobalStyles,
  Wrapper,
  StyledHeader,
  StyledHintText,
  StyledSummaryText,
  StyledFooter,
  Arrow,
  ConfirmationButton,
  CloseButton,
};
